import React from 'react'
import { observer } from 'mobx-react-lite'

import { startOfMonth } from 'date-fns/startOfMonth'
import { endOfMonth } from 'date-fns/endOfMonth'

import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'

import { H3 } from '@mxenabled/mxui'
import { ChevronRight } from '@mxenabled/mx-icons'

import {
  useAccountStore,
  useCategoryStore,
  useFinstrongStore,
  useGlobalStore,
  useGlobalCopyStore,
} from '../../../common'
import { EmptyState } from '../../../common/components/EmptyState'
import DoubleBarChart from '../shared/DoubleBarChart'
import { getSpendingData } from '../../../spending/utils/SpendingData'
import SpendingDonut from '../../../spending/components/SpendingDonut'
import SpendingLegend from '../../../spending/components/SpendingLegend'
import { formatCurrency } from '../../../core/utils/NumberFormatting'

const SpendLessThanYouMakeComponent: React.FC = () => {
  const { accounts } = useAccountStore()
  const { categories, dateRangeCategoryTotals, loadDateRangeCategoryTotals } = useCategoryStore()
  const { monthlySummaries, handleFinstrongWidgetPrimaryCtaClick } = useFinstrongStore()
  const { appDataStore } = useGlobalStore()
  const { finstrong: copy, goals: goalsCopy, spending: spendingCopy } = useGlobalCopyStore()
  const theme = useTheme()

  const [selectedId, setSelectedId] = React.useState('')

  // Load category data for spending chart
  React.useEffect(() => {
    loadDateRangeCategoryTotals(
      accounts,
      startOfMonth(new Date()),
      endOfMonth(new Date()),
    ).finally()
  }, [accounts])

  // Spending data for donut chart
  const spendingData = React.useMemo(
    () => getSpendingData(categories, goalsCopy, theme, dateRangeCategoryTotals),
    [categories, dateRangeCategoryTotals],
  )

  const handleSelected = (id: string) => {
    setSelectedId(selectedId === id ? '' : id)
  }

  const { totalSpending, totalIncome } = React.useMemo(() => {
    return monthlySummaries?.reduce(
      (
        totals: { totalSpending: number; totalIncome: number },
        { standard_spend = 0, income = 0 },
      ) => ({
        totalSpending: totals.totalSpending + standard_spend,
        totalIncome: totals.totalIncome + income,
      }),
      { totalSpending: 0, totalIncome: 0 },
    )
  }, [monthlySummaries])

  //TODO: Add Functionalities for View More Buttons
  return (
    <Stack sx={{ gap: 24 }}>
      {totalIncome > 0 || totalSpending > 0 ? (
        <DoubleBarChart
          {...(appDataStore.appData.client_profile?.allow_trends_widget && {
            buttonLabel: copy.key_indicators_spend.spend_less_than_you_make_drawer.view_more,
          })}
          chartFor="spending_vs_income"
          data={monthlySummaries}
          legendLabelComparison={copy.key_indicators_spend.spend_less_than_you_make_drawer.income}
          legendLabelMain={copy.key_indicators_spend.spend_less_than_you_make_drawer.spending}
          onClick={() => handleFinstrongWidgetPrimaryCtaClick('trends')()}
          title={copy.key_indicators_spend.spend_less_than_you_make_drawer.trends}
          totalComparison={formatCurrency(totalIncome, '0,0.00')}
          totalMain={formatCurrency(totalSpending, '0,0.00')}
        />
      ) : (
        <EmptyState
          header={copy.key_indicators_spend.spend_less_than_you_make_drawer.trends_zero_state_title}
          icon="multiline_chart"
          subText={
            copy.key_indicators_spend.spend_less_than_you_make_drawer.trends_zero_state_description
          }
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: '8px',
            boxShadow: 1,
            p: 24,
            pb: 48,
            '.empty-state-subtext': {
              fontSize: 15,
              lineHeight: '24px',
              mt: 8,
            },
          }}
        />
      )}

      {/* Mini Spending Chart Component */}
      {spendingData.donutData.length > 0 ? (
        <Card
          sx={{
            px: 16,
            py: 24,
          }}
        >
          <Stack
            sx={{
              alignItems: 'center',
              flexDirection: 'row',
              gap: 12,
              justifyContent: 'space-between',
              pb: 8,
              pt: 16,
              px: 16,
            }}
          >
            <H3>{copy.key_indicators_spend.spend_less_than_you_make_drawer.spending}</H3>
            {appDataStore.appData.client_profile?.allow_spending_widget && (
              <Button
                endIcon={<ChevronRight />}
                onClick={() => handleFinstrongWidgetPrimaryCtaClick('spending')()}
                size="small"
                sx={{
                  pl: 8,
                  height: 24,
                }}
                variant="text"
              >
                {copy.key_indicators_spend.spend_less_than_you_make_drawer.view_more}
              </Button>
            )}
          </Stack>
          <Stack
            sx={{ flexDirection: 'row', gap: 12, justifyContent: 'center', pb: 32, pt: 16, px: 24 }}
          >
            <SpendingDonut
              data={spendingData}
              onSelected={handleSelected}
              selectedId={selectedId}
              totalLabel={spendingCopy.total_spending}
            />

            <SpendingLegend
              data={spendingData}
              onSelected={handleSelected}
              selectedId={selectedId}
            />
          </Stack>
        </Card>
      ) : (
        <EmptyState
          header={
            copy.key_indicators_spend.spend_less_than_you_make_drawer.spending_zero_state_title
          }
          icon="donut_small"
          subText={
            copy.key_indicators_spend.spend_less_than_you_make_drawer
              .spending_zero_state_description
          }
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: '8px',
            boxShadow: 1,
            p: 24,
            pb: 48,
            '.empty-state-subtext': {
              fontSize: 15,
              lineHeight: '24px',
              mt: 8,
            },
          }}
        />
      )}
    </Stack>
  )
}

export default observer(SpendLessThanYouMakeComponent)
